import { Control, Controller, FieldPath, FieldValues } from "react-hook-form";
import { View, Text } from "react-native";

import { cn } from "@/shared/lib/utils";
import { Select, type SelectOption } from "./select";

type FormSelectProps<T extends FieldValues> = {
  control: Control<T>;
  name: FieldPath<T>;
  label: string;
  placeholder?: string;
  options: SelectOption[];
  disabled?: boolean;
  isLoading?: boolean;
  className?: string;
  /** When true, shows a search input in the select modal (e.g. for long option lists like cities) */
  searchable?: boolean;
  /** Placeholder for the search input when searchable */
  searchPlaceholder?: string;
  /** Title shown in the select modal header */
  title?: string;
};

export function FormSelect<T extends FieldValues>({
  control,
  name,
  label,
  placeholder = "Select an option",
  options,
  disabled = false,
  isLoading = false,
  className,
  searchable = false,
  searchPlaceholder = "Search...",
  title,
}: FormSelectProps<T>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <View className={cn("mb-4 w-full", className)}>
          <View className="bg-card-event border-b border-white px-4 pt-3 pb-2">
            <Text className="text-muted-foreground mb-1 text-xs">{label}</Text>
            <Select
              value={value}
              onValueChange={onChange}
              options={options}
              placeholder={placeholder}
              disabled={disabled}
              isLoading={isLoading}
              triggerStyle={{
                height: 20,
                minHeight: 0,
                borderWidth: 0,
                backgroundColor: "transparent",
                borderRadius: 0,
                paddingHorizontal: 0,
                paddingVertical: 0,
              }}
              triggerClassName="h-5 min-h-0 border-0 bg-transparent rounded-none px-0 py-0"
              searchable={searchable}
              searchPlaceholder={searchPlaceholder}
              title={title ?? label}
            />
          </View>
          {error && (
            <Text className="text-destructive mt-1 px-4 text-xs">
              {error.message}
            </Text>
          )}
        </View>
      )}
    />
  );
}
